"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, MapPin, Phone } from "lucide-react";

import { GlobalFooter, GlobalHeader } from "./GlobalHeaderFooter";
import AddressInput from "../design-system/molecules/AddressInput";
import { resolveLegacyLink } from "../utils/linkUtils";

type ErrorPageProps = {
    code: string;
    title: string;
    description: string;

};

const features = [
    {
        titre: "Sans engagement",
        descr: "Et sans frais de dossier",
        iconUrl: "https://site-lms.fra1.digitaloceanspaces.com/poignee_de_main_1_42b89ebcbe.png",
    },
    {
        titre: "Livraison rapide",
        descr: "Par notre service de livraison",
        iconUrl: "https://site-lms.fra1.digitaloceanspaces.com/lhorloge_1_c212bbccc3.png",
    },
    {
        titre: "Aides fiscales",
        descr: "Service à la personne",
        iconUrl: "https://site-lms.fra1.digitaloceanspaces.com/Frame_495_d9d3fcf59a.png",
    },
    {
        titre: "Menus élaborés",
        descr: "Par une diététicienne",
        iconUrl: "https://site-lms.fra1.digitaloceanspaces.com/Capa_1_850c4ab1a0.png",
    },
];

const imageUrl = "https://site-lms.fra1.digitaloceanspaces.com/f412274be48daf5bcf016b7d295ee6e911966302_f2e893db49.png"

export default function ErrorPage({
    code,
    title,
    description,
    
}: ErrorPageProps) {
    const pathname = usePathname();
    const needsChrome = pathname.startsWith("/portage-de-repas");

    return (
        <>
            {needsChrome && <GlobalHeader />}

            <main className="error-page w-full bg-white">
                <div
                    className="mx-auto flex min-h-[450px] flex-col justify-between rounded-[40px] border border-gray-300 bg-cover bg-center bg-no-repeat px-4 pb-8 pt-10 lms-container lg:min-h-[580px] lg:px-8 lg:pb-10 lg:pt-12"
                    style={{ backgroundImage: `url(${imageUrl})` }}
                >
                    <div className="text-center">

                        <h1 className="font-playfair text-3xl font-bold text-primaryGreen md:text-4xl lg:text-[44px]">
                            {title}
                        </h1>

                        <p className="mx-auto mt-3 max-w-[850px] font-barlow text-lg font-medium leading-relaxed text-[#343434] lg:text-2xl">
                            {description}
                        </p>
                    </div>

                    <div className="flex justify-center gap-4">
                    
                        <Link
                            href="/"
                            className="inline-flex items-center gap-2 rounded-lg bg-secondary px-7 py-4 text-sm font-bold uppercase text-white transition hover:opacity-90"
                        >
                            <Home size={18} />
                            Retour à l&apos;accueil
                        </Link>
                    </div>
                </div>

                <div className="mx-auto w-full max-w-[1280px] px-4 pb-10 pt-10 lg:px-8">
                    <section>
                        <h2 className="text-center font-playfair text-3xl font-bold text-[#343434] md:text-4xl lg:text-[44px]">
                            Comment pouvons-nous <span className="text-secondary">vous aider</span> ?
                        </h2>

                        <div className="mt-8 grid gap-5 md:grid-cols-2">
                            <div className="flex items-center gap-5 rounded-2xl bg-[#F5F5F5] p-6">
                                <div className="hidden h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-white lg:flex">
                                    <MapPin size={34} strokeWidth={2} className="text-[#75B900]" />
                                </div>

                                <div className="w-full">
                                    <p className="mb-2 text-lg font-bold text-[#343434]">
                                        Trouver mon agence
                                    </p>

                                    <AddressInput />
                                </div>
                            </div>

                            <Link
                                href={resolveLegacyLink("/nous-contater", "main")}
                                className="flex items-center gap-5 rounded-2xl bg-muted p-6 transition hover:bg-[#EEEEEE]"
                            >
                                <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-white">
                                    <Phone size={34} strokeWidth={2} className="text-[#E7194B]" />
                                </div>

                                <div>
                                    <p className="text-lg font-bold text-[#343434]">
                                        Nous contacter
                                    </p>

                                    <p className="mt-1 max-w-[380px] text-base leading-relaxed text-gray-500">
                                        Une question ? Notre équipe est à votre écoute pour vous accompagner.
                                    </p>
                                </div>
                            </Link>
                        </div>
                    </section>

                    <section className="mt-8">
                        <div className="rounded-[24px] bg-[linear-gradient(180deg,#F0FFC5_61.37%,#D8F794_100%)] shadow-xl">
                            <div className="grid grid-cols-1 gap-6 px-6 py-7 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
                                {features.map((feature) => (
                                    <div key={feature.titre} className="flex items-center gap-4">
                                        <div className="relative h-14 w-14 shrink-0">
                                            <Image
                                                src={feature.iconUrl}
                                                alt={feature.titre}
                                                fill
                                                className="object-contain"
                                            />
                                        </div>

                                        <div>
                                            <p className="font-bold text-textColor">{feature.titre}</p>
                                            <p className="mt-1 text-sm text-textColor">{feature.descr}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>
            </main>

            {needsChrome && <GlobalFooter />}
        </>
    );
}